"use client";

import React, { useEffect, useRef } from "react";
import Script from "next/script";
import { FcGoogle } from "react-icons/fc";
import { googleTagManager } from "@/components/common/utils";

type PreferredSourceCallback = Parameters<
  NonNullable<Window["PREFERRED_SOURCE"]>["push"]
>[0];
type PreferredSource = Parameters<PreferredSourceCallback>[0];

// Queues a callback for Google's Preferred Sources library. Before the
// library loads PREFERRED_SOURCE is a plain array it drains on boot; after
// that, push() runs the callback immediately.
const withPreferredSource = (cb: PreferredSourceCallback) => {
  if (!window.PREFERRED_SOURCE) {
    window.PREFERRED_SOURCE = [] as PreferredSourceCallback[];
  }
  window.PREFERRED_SOURCE.push(cb);
};

/**
 * "Set Miles as your Preferred Source" banner.
 *
 * Uses Google's manual control mode so we can render our own styled button
 * instead of Google's default badge. The library is loaded async and the
 * banner has a fixed layout from first paint, so it causes no CLS and never
 * blocks rendering.
 * Docs: https://developers.google.com/search/docs/appearance/preferred-sources
 */
const PreferredSourceBanner = () => {
  const preferredSourceRef = useRef<PreferredSource | null>(null);

  useEffect(() => {
    withPreferredSource((ps) => {
      ps.init({ theme: "light", lang: "en" });
      preferredSourceRef.current = ps;
    });
  }, []);

  const handleClick = () => {
    googleTagManager("gtm_preferred_source_click", {
      page_path: window.location.pathname,
    });
    if (preferredSourceRef.current) {
      preferredSourceRef.current.addPreferredSource();
    } else {
      // Library still loading: the queued call runs as soon as it boots.
      withPreferredSource((ps) => ps.addPreferredSource());
    }
  };

  return (
    // notranslate: keeps Google's in-app page translator from rewriting a
    // React-managed subtree (can crash React's DOM reconciliation).
    <div className="w-full bg-white">
      <div className="main_container mx-auto px-4 md:px-8">
        <div
          className="notranslate not-prose my-6 rounded-2xl bg-[#165496] px-6 py-4 md:px-7 w-full"
          translate="no"
        >
          <Script
            id="google-preferred-sources"
            src="https://news.google.com/swg/js/v1/publisher.js"
            // afterInteractive (still async): addPreferredSource() opens a popup,
            // which browsers only allow inside the click — so the library should
            // be ready before the reader gets to this banner.
            strategy="afterInteractive"
            preferred-sources-control="manual"
          />
          <div className="flex flex-col items-center gap-3 text-center md:flex-row md:justify-between md:text-left">
            <div>
              <p className="text-white text-lg md:text-xl font-semibold leading-snug">
                Set Miles as Your Preferred Source
              </p>
              <p className="hidden md:block text-white/60 text-sm mt-1.5 leading-snug">
                See more updates from Miles on Google
              </p>
            </div>
            <button
              type="button"
              onClick={handleClick}
              className="flex w-full md:w-auto flex-shrink-0 items-center justify-center gap-2.5 rounded-[20px_20px_20px_0] bg-white px-6 py-1.5 text-base font-semibold text-black whitespace-nowrap transition-colors hover:bg-[#F2F2F2]"
            >
              <FcGoogle className="h-6 w-6 flex-shrink-0" aria-hidden="true" />
              Add as a Preferred Source
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferredSourceBanner;
